import { create } from 'zustand';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;
const csrfHeaders = () => ({ 'X-CSRF-Token': csrfToken() });

const useLoyaltyStore = create((set, get) => ({
  // Rewards
  rewards: [],
  rewardsLoading: true,
  rewardSearch: '',
  rewardPartnerFilter: '',

  // Partners
  partners: [],
  partnersLoading: true,

  // Categories
  categories: [],
  categoriesLoading: true,

  // Modal state
  modalOpen: false,
  modalType: null, // 'reward', 'partner', 'category'
  modalMode: 'create', // 'create' or 'edit'
  editingItem: null,

  // Actions
  setRewardSearch: (search) => set({ rewardSearch: search }),
  setRewardPartnerFilter: (filter) => set({ rewardPartnerFilter: filter }),

  openModal: (type, mode = 'create', item = null) => set({
    modalOpen: true,
    modalType: type,
    modalMode: mode,
    editingItem: item
  }),

  closeModal: () => set({
    modalOpen: false,
    modalType: null,
    modalMode: 'create',
    editingItem: null
  }),

  // Fetch rewards
  fetchRewards: async () => {
    set({ rewardsLoading: true });
    try {
      const response = await fetch('/api/admin/loyalty/rewards');
      const json = await response.json();
      if (json.success) set({ rewards: json.data, rewardsLoading: false });
      else set({ rewardsLoading: false });
    } catch (e) {
      set({ rewardsLoading: false });
    }
  },

  // Fetch partners
  fetchPartners: async () => {
    set({ partnersLoading: true });
    try {
      const response = await fetch('/api/admin/loyalty/partners');
      const json = await response.json();
      if (json.success) set({ partners: json.data, partnersLoading: false });
      else set({ partnersLoading: false });
    } catch (e) {
      set({ partnersLoading: false });
    }
  },

  // Fetch categories
  fetchCategories: async () => {
    set({ categoriesLoading: true });
    try {
      const response = await fetch('/api/admin/loyalty/categories');
      const json = await response.json();
      if (json.success) set({ categories: json.data, categoriesLoading: false });
      else set({ categoriesLoading: false });
    } catch (e) {
      set({ categoriesLoading: false });
    }
  },

  // Save reward
  saveReward: async (formData) => {
    const { modalMode, editingItem } = get();
    const url = modalMode === 'edit'
      ? `/api/admin/loyalty/rewards/${editingItem.id}`
      : '/api/admin/loyalty/rewards';
    const method = modalMode === 'edit' ? 'PATCH' : 'POST';

    const response = await fetch(url, { method, headers: csrfHeaders(), body: formData });
    const json = await response.json();
    if (json.success) {
      get().fetchRewards();
      get().closeModal();
    }
    return json;
  },

  // Save partner
  savePartner: async (formData) => {
    const { modalMode, editingItem } = get();
    const url = modalMode === 'edit'
      ? `/api/admin/loyalty/partners/${editingItem.id}`
      : '/api/admin/loyalty/partners';
    const method = modalMode === 'edit' ? 'PATCH' : 'POST';

    const response = await fetch(url, { method, headers: csrfHeaders(), body: formData });
    const json = await response.json();
    if (json.success) {
      get().fetchPartners();
      get().closeModal();
    }
    return json;
  },

  // Save category
  saveCategory: async (formData) => {
    const { modalMode, editingItem } = get();
    const url = modalMode === 'edit'
      ? `/api/admin/loyalty/categories/${editingItem.id}`
      : '/api/admin/loyalty/categories';
    const method = modalMode === 'edit' ? 'PATCH' : 'POST';

    const response = await fetch(url, { method, headers: csrfHeaders(), body: formData });
    const json = await response.json();
    if (json.success) {
      get().fetchCategories();
      get().closeModal();
    }
    return json;
  },

  // Toggle reward status
  toggleRewardStatus: async (id) => {
    const response = await fetch(`/api/admin/loyalty/rewards/${id}/toggle_status`, {
      method: 'PATCH',
      headers: csrfHeaders()
    });
    const json = await response.json();
    if (json.success) get().fetchRewards();
    return json;
  },

  // Toggle reward claimable
  toggleRewardClaimable: async (id) => {
    const response = await fetch(`/api/admin/loyalty/rewards/${id}/toggle_claimable`, {
      method: 'PATCH',
      headers: csrfHeaders()
    });
    const json = await response.json();
    if (json.success) get().fetchRewards();
    return json;
  },

  // Delete
  deleteReward: async (id) => {
    const response = await fetch(`/api/admin/loyalty/rewards/${id}`, {
      method: 'DELETE',
      headers: csrfHeaders()
    });
    const json = await response.json();
    if (json.success) get().fetchRewards();
    return json;
  },

  deletePartner: async (id) => {
    const response = await fetch(`/api/admin/loyalty/partners/${id}`, {
      method: 'DELETE',
      headers: csrfHeaders()
    });
    const json = await response.json();
    if (json.success) get().fetchPartners();
    return json;
  },

  deleteCategory: async (id) => {
    const response = await fetch(`/api/admin/loyalty/categories/${id}`, {
      method: 'DELETE',
      headers: csrfHeaders()
    });
    const json = await response.json();
    if (json.success) get().fetchCategories();
    return json;
  },
}));

export default useLoyaltyStore;
